import { Box, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { routes } from "../../../app/paths";
import { formatCustomerUserOptionLabel } from "../../../common/components/functional/fields/customerUsersAutocomplete/customerUsersAutocompleteUtils";
import InitialsAvatar from "../../../common/components/functional/initialsAvatar/InitialsAvatar";
import TalentStatusIcon, {
  StatusTextStyle,
} from "../../../common/components/functional/talentStatus/TalentStatusIcon";
import { Nullable } from "../../../common/types/nullable";
import { useCustomerNavigation } from "../../../hooks/useCustomerNavigation";
import { ParticipantOption, ParticipantType } from "../types/common";

type Props = {
  participant: Nullable<ParticipantOption>;
  disableTopMargin?: boolean;
};

const participantLinkStyle = {
  textDecoration: "none",
  display: "flex",
  minWidth: 0,
  width: "100%",
  overflow: "hidden",
} as const;

const ConversationParticipantHeader = ({
  participant,
  disableTopMargin = false,
}: Props) => {
  const { navigateToCustomerByUserId, isNavigationLoading } =
    useCustomerNavigation();

  if (!participant) return null;

  const isTalent = participant.type === ParticipantType.TALENT;
  const city = participant.city ? ` – ${participant.city}` : "";
  const label = isTalent
    ? `${participant.name}${city}`
    : formatCustomerUserOptionLabel(
        participant.name,
        participant.customerCompanyName,
      );
  // TODO: do we need to show work locations?

  const content = (
    <Box
      display="flex"
      alignItems="center"
      mt={disableTopMargin ? 0 : "6px"}
      gap="8px"
      minWidth={0}
      width="100%"
      overflow="hidden"
      pr="4px"
    >
      <InitialsAvatar
        name={participant.name}
        sx={{ flexShrink: 0, minWidth: 34, minHeight: 34 }}
      />
      <Box display="flex" alignItems="center" minWidth={0} overflow="hidden">
        <Tooltip title={label}>
          <Typography
            color="text.primary"
            fontWeight={600}
            noWrap
            sx={{
              flex: "0 1 auto",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              pr: "4px",
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        {isTalent && participant.talentStatus && (
          <Box sx={{ flexShrink: 0 }}>
            <TalentStatusIcon
              status={participant.talentStatus}
              statusTextStyle={StatusTextStyle.TOOLTIP}
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  if (isTalent) {
    return (
      <Link
        style={participantLinkStyle}
        target="_blank"
        rel="noopener noreferrer"
        to={routes.talentProfile(participant.id)}
      >
        {content}
      </Link>
    );
  }

  if (participant.type !== ParticipantType.CUSTOMER) return content;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigateToCustomerByUserId(participant.id)}
      disabled={isNavigationLoading}
      sx={{
        ...participantLinkStyle,
        border: "none",
        background: "none",
        padding: 0,
        color: "inherit",
        font: "inherit",
        textAlign: "left",
        cursor: isNavigationLoading ? "progress" : "pointer",
      }}
    >
      {content}
    </Box>
  );
};

export default ConversationParticipantHeader;
